import React, { useState, useEffect } from "react";
import {
  TextField,
  Typography,
  Checkbox,
  Slider,
  MenuItem,
} from "@mui/material";
import Grid2 from '@mui/material/Grid2';
import BasePaper from "./BasePaper";
import { useTheme } from "@mui/material/styles";
import { addPeopleToNode } from "components/Flow/Controls/AddPeopleToNode";
import { addAddressesToNode } from "components/Flow/Controls/AddAddressesToNode";
import { addCommunityToNode } from "components/Flow/Controls/AddCommunityToNode";
import { peopleAPI } from "api/Community/PeopleAPI";
import { addressesAPI } from "api/Community/AddressesAPI";
import { communityAPI, getOutputNodes } from "api/Community/CommunityAPI";

interface ValueInputProps {
  label: string | null;
  type: string;
  value?: string | number | boolean | null;
  metadata?: any;        // <-- JSON metadata contains min/max/step/options
  onChange?: (value: any) => void;
  direction?: "row" | "column";
  nodeId: number;
  nodeAPI: { updateField: (id: number, field: string, value: any) => Promise<any> };
  currentPeople: any[];
  currentAddresses: any[];
}

const ValueInput: React.FC<ValueInputProps> = ({
  label,
  type,
  value,
  metadata = {},
  onChange,
  direction = "row",
  nodeId,
  nodeAPI,
  currentPeople,
  currentAddresses,
}) => {
  
  const theme = useTheme();
  const [localValue, setLocalValue] = useState<any>(() => {
    if (type === "date") return toDateInput(value);
    if (type === "datetime") return toDateTimeLocalInput(value);
    return value;
  });

  // Sync if backend updates value
  useEffect(() => {
    if (type === "date") {
      setLocalValue(toDateInput(value));
    } else if (type === "datetime") {
      setLocalValue(toDateTimeLocalInput(value));
    } else {
      setLocalValue(value);
    }
  }, [value, type]);


  function toDateInput(value: any): string {
    if (!value) return "";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10); // YYYY-MM-DD
  }

  function toDateTimeLocalInput(value: any): string {
    if (!value) return "";

    // Already correct
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) {
      return value;
    }

    const d = new Date(value);
    if (isNaN(d.getTime())) return "";

    const pad = (n: number) => n.toString().padStart(2, "0");

    return (
      `${d.getFullYear()}-` +
      `${pad(d.getMonth() + 1)}-` +
      `${pad(d.getDate())}T` +
      `${pad(d.getHours())}:` +
      `${pad(d.getMinutes())}`
    );
  }



  // Pull constraints from metadata JSON
  const min = metadata.min ?? undefined;
  const max = metadata.max ?? undefined;
  const step = metadata.step ?? (type === "float" ? "any" : 1);

  // Convert metadata.options into array
  const selectorOptions = (() => {
    if (!metadata || !metadata.options) return [];

    const opts = metadata.options;

    // Array format
    if (Array.isArray(opts)) {
      return opts
        .filter(o => o && typeof o.value !== "undefined")
        .map(o => ({ value: o.value, label: o.label ?? String(o.value) }));
    }

    // Object format
    if (typeof opts === "object") {
      return Object.entries(opts).map(([value, label]) => ({
        value,
        label: label ?? value,
      }));
    }

    return [];
  })();


  const onButtonClick = () => {
    if (!metadata?.buttonAction) return;

    if (metadata.buttonAction === "addPeople") {
      addPeopleToNode({
        nodeId: nodeId,
        fieldName: "people",
        peopleAPI: peopleAPI,
        nodeAPI: nodeAPI,
        currentPeople: currentPeople ?? [],
      });
    }

    if (metadata.buttonAction === "addAddresses") {
      addAddressesToNode({
        nodeId: nodeId,
        fieldName: "addresses",
        addressesAPI: addressesAPI,
        nodeAPI: nodeAPI,
        currentAddresses: currentAddresses ?? [],
      });
    }


    if (metadata.buttonAction === "addCommunity") {
      addCommunityToNode({
        nodeId: nodeId,
        fieldName: "people",
        communityAPI: communityAPI,
        getOutputNodes: getOutputNodes,
        nodeAPI: nodeAPI,
        currentPeople: currentPeople ?? [],
      });
    }
  };

  const handleInputChange = (e: any) => {
    if (type === "boolean") {
      const checked = e.target.checked;
      setLocalValue(checked);
      onChange?.(checked);
    } else {
      const val = e.target.value;
      setLocalValue(val);
    }
  };

  const handleBlur = () => {
    onChange?.(localValue);
  };

  return (
    <BasePaper
      direction={direction}
      backgroundColor={theme.custom.nodes.background.functionality.dark}
    >
      {/* Show the standard label for everything except button + displayText */}
      {label && type !== "button" && type !== "displayText" && (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            textAlign: direction === "column" ? "center" : "left",
            mb: direction === "column" ? 0.5 : 0,
          }}
        >
          {label}
        </Typography>
      )}

      {/* DISPLAY TEXT (non-editable) */}
      {type === "displayText" && (
        <Typography
          variant="body1"
          sx={{
            textAlign: direction === "column" ? "center" : "left",
            width: "100%",
            whiteSpace: "pre-wrap", // supports line breaks
          }}
        >
          {label ?? ""}
        </Typography>
      )}
      

      {/* BOOLEAN */}
      {type === "boolean" && (
        <Checkbox checked={Boolean(localValue)} onChange={handleInputChange} />
      )}

      {/* FLOAT / INTEGER */}
      {(type === "float" || type === "integer") && (
        <TextField
          size="small"
          type="number"
          value={localValue ?? ""}
          onChange={handleInputChange}
          onBlur={handleBlur}
          inputProps={{
            min,
            max,
            step,
            style: { textAlign: "right", width: "80px" },
          }}
        />
      )}

      {/* PERCENTAGE */}
      {type === "percentage" && (
        <Grid2 container alignItems="center" spacing={1}>
          <Grid2>
            <Slider
              value={typeof localValue === "number" ? localValue : 0}
              min={min ?? 0}
              max={max ?? 100}
              step={step ?? 1}
              valueLabelDisplay="auto"
              onChange={(_, newVal) => setLocalValue(newVal)}
              onChangeCommitted={(_, newVal) => onChange?.(newVal)}
            />
          </Grid2>

          <Grid2>
            <TextField
              size="small"
              type="number"
              value={localValue ?? ""}
              onChange={handleInputChange}
              onBlur={handleBlur}
              inputProps={{
                min,
                max,
                step,
                style: { width: "60px", textAlign: "right" },
              }}
            />
          </Grid2>

          <Grid2>
            <Typography variant="body2">%</Typography>
          </Grid2>
        </Grid2>
      )}

      {/* TEXT */}
      {type === "text" && (
        <TextField
          size="small"
          value={localValue ?? ""}
          onChange={handleInputChange}
          onBlur={handleBlur}
          fullWidth
        />
      )}

      {/* TEXTAREA */}
      {type === "textarea" && (
        <TextField
          size="small"
          value={localValue ?? ""}
          onChange={handleInputChange}
          onBlur={handleBlur}
          multiline
          minRows={3}
          fullWidth
        />
      )}

      {/* DATE */}
      {type === "date" && (
        <TextField
          type="date"
          size="small"
          value={localValue ?? ""}
          onChange={handleInputChange}
          onBlur={handleBlur}
          InputLabelProps={{ shrink: true }}
        />
      )}

      {/* DATETIME */}
      {type === "datetime" && (
        <TextField
          type="datetime-local"
          size="small"
          value={localValue ?? ""}
          onChange={handleInputChange}
          onBlur={handleBlur}
          InputLabelProps={{ shrink: true }}
        />
      )}

      {/* SELECTOR */}
      {type === "selector" && (
        <div
          // Mark this as a non-draggable, non-pannable area for React Flow
          data-no-drag
          data-no-pan
          className="nodrag nopan"
          style={{ pointerEvents: "all", width: "100%" }}
          onPointerDown={(e) => {
            // Stop the event from bubbling up to React Flow's drag logic
            e.stopPropagation();
          }}
        >
          <TextField
            select
            size="small"
            fullWidth
            value={localValue ?? ""}
            onChange={(e) => {
              setLocalValue(e.target.value);
              onChange?.(e.target.value);
            }}
            SelectProps={{
              // Let the menu render in a portal (usually safest with React Flow)
              MenuProps: {
                disablePortal: false,
              },
            }}
          >
            {selectorOptions.map((opt: any) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label ?? opt.value}
              </MenuItem>
            ))}
          </TextField>
        </div>
      )}

      {/* Button */}
      {type === "button" && (
        <button
          style={{
            padding: "6px 12px",
            borderRadius: "4px",
            border: "none",
            backgroundColor: theme.custom.nodes.background.main.dark,
            color: theme.custom.nodes.text.primary.dark,
            cursor: "pointer",
            alignContent: "center",
          }}
          onClick={onButtonClick}
        >
          {label || "Button"}
        </button>
      )}





    </BasePaper>
  );
};

export default ValueInput;
